// =====================================================================================
// FILE: src/components/table/SortableRow.jsx (WITH LOCK FEATURE + NEW COLUMNS + CORNERTVC)
// =====================================================================================

import React, { useState, useEffect } from "react";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { THEME, FONTS } from "../../constants/theme";
import { statusRowColor, statusBorderColor, stop, isRowLocked } from "../../utils/statusColors";
import { uniqArr, cleanMulti } from "../../utils/categoryUtils";
import { assetIdOf } from "../../utils/assetUtils";
import InlineAssetPicker from "../pickers/InlineAssetPicker";
import MultiSelectInline from "../form/MultiSelectInline";
import { showAlert } from "../ui/Toast";
import { highlightText } from "../ui/SearchBar";
import ContextMenu from "../ui/ContextMenu";
import { usePresence, getInitials } from "../../context/PresenceContext";

// ✅ Context menu for asset cells - Copy ID + row actions
function AssetContextMenu({ x, y, onClose, onCopyId, onCopy, onMove, onDelete, assetTitle, item, locked, categories }) {
  const menuRef = React.useRef(null);
  const [showMoveSubmenu, setShowMoveSubmenu] = React.useState(false);

  React.useEffect(() => {
    const handleClickOutside = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        onClose();
      }
    };
    const handleEscape = (e) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("keydown", handleEscape);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleEscape);
    };
  }, [onClose]);

  // Extract ID for display
  const parts = (assetTitle || "").split("_");
  const displayId = parts.length >= 2 ? `${parts[0]}_${parts[1]}` : assetTitle;

  // Filter out current category from move options
  const moveCategories = (categories || []).filter(cat => cat !== item?.category);

  const menuItemStyle = (disabled = false, danger = false) => ({
    display: "flex",
    alignItems: "center",
    gap: 10,
    padding: "10px 16px",
    fontSize: FONTS.sm,
    color: disabled 
      ? "rgba(255,255,255,0.3)" 
      : danger 
        ? "#FF6B6B" 
        : "rgba(255,255,255,0.9)",
    cursor: disabled ? "not-allowed" : "pointer",
    background: "transparent",
    border: "none",
    width: "100%",
    textAlign: "left",
    fontFamily: FONTS.family,
    transition: "background 0.1s ease",
  });

  return (
    <div
      ref={menuRef}
      style={{
        position: "fixed",
        top: y,
        left: x,
        background: THEME.dark,
        borderRadius: THEME.radius,
        boxShadow: "0 8px 32px rgba(0,0,0,0.3), 0 2px 8px rgba(0,0,0,0.2)",
        border: "1px solid rgba(255,255,255,0.1)",
        padding: "6px 0",
        minWidth: 180,
        zIndex: 10001,
        fontFamily: FONTS.family,
        animation: "contextMenuFadeIn 0.15s ease",
      }}
    >
      <style>{`
        @keyframes contextMenuFadeIn {
          from { opacity: 0; transform: scale(0.95); }
          to { opacity: 1; transform: scale(1); }
        }
      `}</style>
      
      {/* Header showing the Asset ID */}
      <div style={{
        padding: "6px 16px 8px",
        fontSize: FONTS.xs,
        color: "rgba(255,255,255,0.5)",
        borderBottom: "1px solid rgba(255,255,255,0.1)",
        marginBottom: 4,
      }}>
        Asset: <span style={{ color: THEME.primary, fontWeight: FONTS.semibold }}>{displayId}</span>
      </div>

      {/* Copy ID button */}
      <button
        onClick={() => { onCopyId(); onClose(); }}
        style={menuItemStyle(false)}
        onMouseEnter={(e) => (e.currentTarget.style.background = "rgba(255,255,255,0.1)")}
        onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
      >
        <span style={{ width: 18, textAlign: "center" }}>🔗</span>
        <span>Copy Asset ID</span>
      </button>

      {/* Divider */}
      <div style={{
        height: 1,
        background: "rgba(255,255,255,0.1)",
        margin: "6px 0",
      }} />

      {/* Row name */}
      <div style={{
        padding: "6px 16px 8px",
        fontSize: FONTS.xs,
        color: "rgba(255,255,255,0.5)",
      }}>
        Row: <span style={{ color: "white" }}>{item?.name || "Untitled"}</span>
      </div>

      {/* Duplicate */}
      <button
        style={menuItemStyle(locked)}
        onClick={() => { if (!locked) { onCopy(); onClose(); } }}
        onMouseEnter={(e) => !locked && (e.currentTarget.style.background = "rgba(255,255,255,0.1)")}
        onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
      >
        <span style={{ width: 18, textAlign: "center" }}>📋</span>
        <span>Duplicate Row</span>
        {locked && <span style={{ marginLeft: "auto", fontSize: 12 }}>🔒</span>}
      </button>

      {/* Move - with submenu */}
      <div 
        style={{ position: "relative" }}
        onMouseEnter={() => setShowMoveSubmenu(true)}
        onMouseLeave={() => setShowMoveSubmenu(false)}
      >
        <button
          style={{
            ...menuItemStyle(locked),
            justifyContent: "space-between",
          }}
          onMouseEnter={(e) => !locked && (e.currentTarget.style.background = "rgba(255,255,255,0.1)")}
          onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <span style={{ width: 18, textAlign: "center" }}>📁</span>
            <span>Move to...</span>
          </div>
          <span style={{ opacity: 0.5, fontSize: 12 }}>▶</span>
          {locked && <span style={{ fontSize: 12 }}>🔒</span>}
        </button>

        {/* Move Submenu */}
        {showMoveSubmenu && !locked && moveCategories.length > 0 && (
          <div style={{
            position: "absolute",
            left: "100%",
            top: 0,
            background: THEME.dark,
            borderRadius: THEME.radius,
            boxShadow: "0 8px 32px rgba(0,0,0,0.3), 0 2px 8px rgba(0,0,0,0.2)",
            border: "1px solid rgba(255,255,255,0.1)",
            padding: "6px 0",
            minWidth: 180,
            maxHeight: 300,
            overflowY: "auto",
            zIndex: 10002,
          }}>
            <div style={{
              padding: "6px 16px 8px",
              fontSize: FONTS.xs,
              color: "rgba(255,255,255,0.5)",
              borderBottom: "1px solid rgba(255,255,255,0.1)",
              marginBottom: 4,
            }}>
              Select category
            </div>
            {moveCategories.map((cat) => (
              <button
                key={cat}
                style={menuItemStyle(false)}
                onClick={() => {
                  onMove(item, cat);
                  onClose();
                }}
                onMouseEnter={(e) => (e.currentTarget.style.background = "rgba(255,255,255,0.1)")}
                onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
              >
                <span style={{ width: 18, textAlign: "center" }}>→</span>
                <span style={{
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  maxWidth: 140,
                }}>
                  {cat}
                </span>
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Divider */}
      <div style={{
        height: 1,
        background: "rgba(255,255,255,0.1)",
        margin: "6px 0",
      }} />

      {/* Delete */}
      <button
        style={menuItemStyle(locked, true)}
        onClick={() => { if (!locked) { onDelete(); onClose(); } }}
        onMouseEnter={(e) => !locked && (e.currentTarget.style.background = "rgba(255,107,107,0.15)")}
        onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
      >
        <span style={{ width: 18, textAlign: "center" }}>🗑️</span>
        <span>Delete Row</span>
        {locked && <span style={{ marginLeft: "auto", fontSize: 12 }}>🔒</span>}
      </button>
    </div>
  );
}

// ✅ Helper function to format filename for display
// Now shows full filename for better searchability
// Example: "CAR_1KE0G30Y2_Marvel Superscreen_Side Panel_720.jpg" 
// Can optionally remove file extension for cleaner display
const formatAssetTitle = (filename, removeExtension = true) => {
  if (!filename) return filename;
  
  // Remove file extension if requested
  if (removeExtension) {
    const lastDot = filename.lastIndexOf('.');
    if (lastDot > 0) {
      return filename.substring(0, lastDot);
    }
  }
  
  return filename;
};

// ✅ Extract Lumen ID from asset title (e.g., "STK_6D2PXAJA" from "STK_6D2PXAJA_filename.mp4")
const extractLumenId = (filename) => {
  if (!filename) return filename;
  
  const parts = filename.split('_');
  if (parts.length >= 2) {
    // Format: CLUB_ID_filename or CLUB_ID
    return `${parts[0]}_${parts[1]}`;
  }
  
  // Fallback to full title (without extension)
  return formatAssetTitle(filename);
};

// ============================================================================
// STANDALONE InlineAsset COMPONENT
// ============================================================================
// IMPORTANT: This MUST be defined outside SortableRow.
// Defining it inside causes React to see a new component type on every parent
// re-render, which unmounts/remounts InlineAssetPicker and destroys its
// internal search state (the "search resets after 1 char" bug).
// ============================================================================
function InlineAssetStable({
  col,
  currentId,
  patchKey,
  // dependencies that were previously closures:
  assetCache,
  editingCell,
  itemId,
  item,
  isBusy,
  locked,
  setEditingCell,
  onInlineSave,
  onCopy,
  onMove,
  onDelete,
  categories,
  searchTerm,
}) {
  const getAsset = (id) =>
    id ? assetCache.get(id) || { id, title: id, destination: "", thumb: "" } : null;

  const current = getAsset(currentId);
  const [assetContextMenu, setAssetContextMenu] = useState(null);

  const inlineThumbStyle = {
    width: 32,
    height: 32,
    borderRadius: 6,
    objectFit: "contain",
    border: "1px solid #eee",
    background: "#fafafa",
    flex: "0 0 auto",
    transition: "transform 0.2s ease",
  };

  const isEditing = editingCell?.id === itemId && editingCell?.col === col;

  const handleAssetContextMenu = (e) => {
    if (!current) return;
    e.preventDefault();
    e.stopPropagation();
    setAssetContextMenu({ x: e.clientX, y: e.clientY });
  };

  const copyAssetId = async () => {
    if (!current?.title) return;
    const title = current.title;
    const parts = title.split('_');
    let assetId = title;
    if (parts.length >= 2) {
      assetId = `${parts[0]}_${parts[1]}`;
    }
    try {
      await navigator.clipboard.writeText(assetId);
      showAlert(`Copied: ${assetId}`, "success");
    } catch (err) {
      const textArea = document.createElement("textarea");
      textArea.value = assetId;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand("copy");
      document.body.removeChild(textArea);
      showAlert(`Copied: ${assetId}`, "success");
    }
    setAssetContextMenu(null);
  };

  const buildTooltip = (asset) => {
    if (!asset) return locked ? "🔒 Locked" : "";
    const lines = [asset.title || "(Untitled)"];
    if (asset.destination) lines.push(`📁 ${asset.destination}`);
    if (asset.duration && asset.duration > 0) {
      const duration = typeof asset.duration === 'number' ? asset.duration : parseFloat(asset.duration);
      if (!isNaN(duration) && duration > 0) {
        if (duration >= 60) {
          const mins = Math.floor(duration / 60);
          const secs = Math.round(duration % 60);
          lines.push(`⏱️ ${mins}:${secs.toString().padStart(2, '0')}`);
        } else {
          lines.push(`⏱️ ${duration.toFixed(1)}s`);
        }
      } else {
        lines.push("⏱️ Static (no duration)");
      }
    } else {
      lines.push("⏱️ Static (no duration)");
    }
    const hasAlpha = asset.hasAlpha === true;
    const hasAudio = asset.hasAudio === true;
    if (hasAlpha && hasAudio) lines.push("🎨 Has Alpha  •  🔊 Has Audio");
    else if (hasAlpha && !hasAudio) lines.push("🎨 Has Alpha  •  🔇 No Audio");
    else if (!hasAlpha && hasAudio) lines.push("⬜ No Alpha  •  🔊 Has Audio");
    else lines.push("⬜ No Alpha  •  🔇 No Audio");
    if (locked) lines.push("🔒 Built by operator — locked");
    return lines.join("\n");
  };

  const displayTitle = current ? extractLumenId(current.title) : null;

  return (
    <div
      style={{
        width: "100%",
        direction: "ltr",
        unicodeBidi: "isolate",
        writingMode: "horizontal-tb",
        textAlign: "left",
        minWidth: 0,
        cursor: locked ? "not-allowed" : "pointer",
        opacity: locked ? 0.6 : 1,
        position: "relative",
      }}
      onContextMenu={handleAssetContextMenu}
    >
      {assetContextMenu && (
        <AssetContextMenu
          x={assetContextMenu.x}
          y={assetContextMenu.y}
          onClose={() => setAssetContextMenu(null)}
          onCopyId={copyAssetId}
          onCopy={() => onCopy(item)}
          onMove={onMove}
          onDelete={() => onDelete(item)}
          assetTitle={current?.title}
          item={item}
          locked={locked}
          categories={categories}
        />
      )}

      <div
        role="button"
        tabIndex={0}
        onClick={(e) => {
          if (isEditing) return;
          if (locked) {
            stop(e);
            showAlert("This content has been built by the operator and is locked. Contact your Lumen8 content manager to unlock it.", "warning");
            return;
          }
          stop(e);
          if (isBusy) return;
          setEditingCell({ id: itemId, col, isOpen: true });
        }}
        style={{
          padding: isEditing && current ? "4px 8px" : 0,
          borderRadius: isEditing ? THEME.radius : 0,
          background: isEditing && current ? THEME.primaryLight : "transparent",
          border: isEditing && current ? `1px solid ${THEME.primary}` : "none",
          marginBottom: isEditing ? 4 : 0,
        }}
        title={current ? buildTooltip(current) : "Click to select content from the library.\nContent is optional – not every row needs an asset linked."}
      >
        {current ? (
          <div style={{ minWidth: 0 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8, minWidth: 0 }}>
              {current.thumb ? (
                <img
                  src={current.thumb}
                  alt=""
                  style={inlineThumbStyle}
                  onMouseEnter={(e) => { e.currentTarget.style.transform = "scale(2.5)"; e.currentTarget.style.zIndex = "100"; e.currentTarget.style.position = "relative"; }}
                  onMouseLeave={(e) => { e.currentTarget.style.transform = "scale(1)"; e.currentTarget.style.zIndex = "1"; }}
                  onError={(e) => { e.currentTarget.style.display = "none"; }}
                />
              ) : (
                <div style={inlineThumbStyle} />
              )}
              <div
                style={{
                  fontWeight: FONTS.semibold,
                  fontSize: FONTS.sm,
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  minWidth: 0,
                  color: isEditing ? THEME.primary : THEME.text,
                }}
              >
                {highlightText(displayTitle, searchTerm)}
              </div>
            </div>
            {current.destination && !isEditing ? (
              <div
                style={{
                  fontSize: FONTS.xs,
                  opacity: 0.7,
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
              >
                {highlightText(current.destination, searchTerm)}
              </div>
            ) : null}
          </div>
        ) : (
          <div style={{ opacity: 0.55, fontSize: FONTS.sm }}>Click to select…</div>
        )}
      </div>

      {isEditing && (
        <InlineAssetPicker
          current={current}
          disabled={isBusy}
          assetCache={assetCache}
          columnKey={col}
          onPick={(asset) => {
            onInlineSave(itemId, { [patchKey]: assetIdOf(asset) });
            setEditingCell(null);
          }}
          onClear={() => {
            onInlineSave(itemId, { [patchKey]: null });
            setEditingCell(null);
          }}
          onClose={() => {
            setEditingCell(null);
          }}
        />
      )}
    </div>
  );
}

// ============================================================================
// STABLE INLINE EDITORS (module scope)
// Defined OUTSIDE SortableRow so their React component identity is stable
// across renders. The draft being edited lives in LOCAL state here, so typing
// a character re-renders ONLY this editor — it does not call setEditingCell,
// so the whole builder no longer re-renders and the <input> no longer remounts
// on every keystroke (that remount was what threw the caret to the end).
// Commit still flows through onInlineSave exactly as before, so the
// bidirectional sync is unaffected.
// ============================================================================
const InlineTextEditor = React.memo(function InlineTextEditor({ initialValue, onCommit, onCancel }) {
  const [draft, setDraft] = React.useState(initialValue ?? "");
  const ref = React.useRef(null);

  React.useEffect(() => {
    const el = ref.current;
    if (!el) return;
    el.focus();
    const len = el.value.length;
    try { el.setSelectionRange(len, len); } catch (_) {}
  }, []);

  return (
    <input
      ref={ref}
      value={draft}
      onChange={(e) => setDraft(e.target.value)}
      onBlur={() => onCommit(draft)}
      onKeyDown={(e) => {
        if (e.key === "Escape") { stop(e); onCancel(); }
        if (e.key === "Enter") { stop(e); onCommit(draft); }
      }}
      dir="ltr"
      lang="en"
      spellCheck
      style={{
        width: "100%",
        border: `1px solid ${THEME.line}`,
        borderRadius: 12,
        padding: "8px 10px",
        fontSize: 13,
        color: THEME.text,
        boxSizing: "border-box",
        direction: "ltr",
        unicodeBidi: "isolate",
        writingMode: "horizontal-tb",
        textAlign: "left",
      }}
    />
  );
});

const InlineNumberEditor = React.memo(function InlineNumberEditor({ initialValue, min = 0, step = 1, onCommit, onCancel }) {
  const [draft, setDraft] = React.useState(String(initialValue ?? ""));
  const ref = React.useRef(null);
  React.useEffect(() => { ref.current?.focus(); }, []);

  return (
    <input
      ref={ref}
      type="number"
      min={min}
      step={step}
      value={draft}
      onChange={(e) => setDraft(e.target.value)}
      onBlur={() => onCommit(draft)}
      onKeyDown={(e) => {
        if (e.key === "Escape") { stop(e); onCancel(); }
        if (e.key === "Enter") { stop(e); onCommit(draft); }
      }}
      dir="ltr"
      style={{
        width: "100%",
        border: `1px solid ${THEME.line}`,
        borderRadius: 12,
        padding: "8px 10px",
        fontSize: 13,
        boxSizing: "border-box",
        direction: "ltr",
        unicodeBidi: "isolate",
        writingMode: "horizontal-tb",
        textAlign: "left",
      }}
    />
  );
});

// Read-only display + click-to-edit wrapper for single-line text cells.
// Module scope = stable type, so an incidental SortableRow re-render mid-edit
// won't remount the editor either.
const RowTextCell = React.memo(function RowTextCell({
  item, col, field, value, locked, isBusy, editingCell, setEditingCell, onInlineSave, searchTerm,
}) {
  const isEditing = editingCell?.id === item.id && editingCell?.col === col;

  const beginEdit = (e) => {
    stop(e);
    if (locked) {
      showAlert("This content has been built by the operator and is locked. Contact your Lumen8 content manager to unlock it.", "warning");
      return;
    }
    if (isBusy) return;
    setEditingCell({ id: item.id, col });
  };

  const commit = async (draft) => {
    const next = String(draft ?? "");
    setEditingCell(null);
    if (String(value ?? "") === next) return;
    await onInlineSave(item.id, { [field]: next });
  };

  const cancel = () => setEditingCell(null);

  if (!isEditing) {
    return (
      <div
        role="button"
        tabIndex={0}
        onClick={beginEdit}
        dir="ltr"
        title={locked ? "🔒 Built by operator — contact Lumen8 to unlock" : (value ?? "")}
        style={{
          width: "100%",
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis",
          cursor: locked ? "not-allowed" : "text",
          opacity: locked ? 0.6 : 1,
          direction: "ltr",
          unicodeBidi: "isolate",
          writingMode: "horizontal-tb",
          textAlign: "left",
        }}
      >
        {value ? highlightText(value, searchTerm) : <span style={{ opacity: 0.5, cursor: "text" }}>Click to edit…</span>}
      </div>
    );
  }

  return <InlineTextEditor initialValue={value} onCommit={commit} onCancel={cancel} />;
});

const RowNumberCell = React.memo(function RowNumberCell({
  item, col, field, value, min = 0, step = 1, locked, isBusy, editingCell, setEditingCell, onInlineSave,
}) {
  const isEditing = editingCell?.id === item.id && editingCell?.col === col;
  const v = value ?? 0;

  const beginEdit = (e) => {
    stop(e);
    if (locked) {
      showAlert("This content has been built by the operator and is locked. Contact your Lumen8 content manager to unlock it.", "warning");
      return;
    }
    if (isBusy) return;
    setEditingCell({ id: item.id, col });
  };

  const commit = async (draft) => {
    const raw = String(draft ?? "").trim();
    const parsed = raw === "" ? 0 : Number(raw);
    const nextVal = Number.isFinite(parsed) ? parsed : 0;
    setEditingCell(null);
    if (Number(v || 0) === nextVal) return;
    await onInlineSave(item.id, { [field]: nextVal });
  };

  const cancel = () => setEditingCell(null);

  if (!isEditing) {
    return (
      <div
        role="button"
        tabIndex={0}
        onClick={beginEdit}
        dir="ltr"
        title={locked ? "🔒 Built by operator — contact Lumen8 to unlock" : String(v)}
        style={{
          width: "100%",
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis",
          cursor: locked ? "not-allowed" : "text",
          opacity: locked ? 0.6 : 1,
          direction: "ltr",
          unicodeBidi: "isolate",
          writingMode: "horizontal-tb",
          textAlign: "left",
        }}
      >
        {v ? String(v) : <span style={{ opacity: 0.5 }}>Click to edit…</span>}
      </div>
    );
  }

  return <InlineNumberEditor initialValue={String(v)} min={min} step={step} onCommit={commit} onCancel={cancel} />;
});

export default function SortableRow({
  item,
  assetCache,
  isBusy,
  editingCell,
  setEditingCell,
  onCopy,
  onMove,
  onDelete,
  onInlineSave,
  visibleKeys,
  stickyLeftMap,
  template,
  isIptvMode,
  isRowLocked: isRowLockedFunc,
  iptvOptions,
  isSelected,
  onToggleSelect,
  searchTerm,
  categories = [],
}) {
  const {
    attributes,
    listeners,
    setNodeRef,
    setActivatorNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: item.id });

  const rowBg = statusRowColor(item.contentStatus);
  const rowBorder = statusBorderColor(item.contentStatus);
  const locked = isRowLockedFunc ? isRowLockedFunc(item.contentStatus) : isRowLocked(item.contentStatus);
  
  // ✅ Check if this is an Example Content placeholder row
  const isExampleContent = String(item.contentStatus || "").toLowerCase().includes("example content");

  // ✅ Presence - track who's editing this row
  const { isRowBeingEdited, setEditingRow } = usePresence();
  const editingUser = isRowBeingEdited(item.id);
  
  // ✅ Broadcast when we start/stop editing this row
  useEffect(() => {
    const isEditingThisRow = editingCell?.id === item.id;
    if (isEditingThisRow) {
      setEditingRow(item.id);
    }
    // Note: We don't clear on unmount since clearEditingRow is called when editingCell becomes null
  }, [editingCell?.id, item.id, setEditingRow]);

  // ✅ Context menu state
  const [contextMenu, setContextMenu] = useState(null);
  
  // ✅ Row hover state for showing history icon
  const [rowHovered, setRowHovered] = useState(false);
  
  // ✅ Comment popup state for viewing full Lumen8 Comments
  const [showCommentPopup, setShowCommentPopup] = useState(false);

  const handleContextMenu = (e) => {
    e.preventDefault();
    setContextMenu({ x: e.clientX, y: e.clientY });
  };

  const closeContextMenu = () => setContextMenu(null);

  const rowStyle = {
    transform: CSS.Transform.toString(transform),
    transition,
    display: "grid",
    gridTemplateColumns: template,
    alignItems: "stretch",
    background: isExampleContent ? 'rgba(148, 163, 184, 0.08)' : rowBg,
    border: isExampleContent ? '1px dashed rgba(148, 163, 184, 0.4)' : `1px solid ${rowBorder}`,
    borderLeft: isExampleContent ? '5px dashed rgba(148, 163, 184, 0.5)' : `5px solid ${rowBorder}`,
    borderRadius: THEME.radiusMd,
    overflow: "visible",
    opacity: isExampleContent ? 0.5 : (isDragging ? 0.96 : 1),
    boxShadow: isExampleContent ? 'none' : (isDragging ? THEME.rowShadowDrag : THEME.rowShadow),
    position: "relative",
    fontFamily: FONTS.family,
    marginBottom: 6,
  };

  const cellBase = {
    fontSize: FONTS.sm,
    fontWeight: FONTS.regular,
    color: isExampleContent ? 'rgba(148, 163, 184, 0.8)' : THEME.text,
    padding: "10px 12px",
    borderRight: `1px solid ${THEME.lineLight}`,
    display: "flex",
    alignItems: "center",
    minHeight: 44,
    overflow: "visible",
    boxSizing: "border-box",
    minWidth: 0,
    position: "relative",
  };

  const stickyStyle = (key) =>
    key === "select" || key === "drag" || key === "cue"
      ? {
          position: "sticky",
          left: stickyLeftMap[key],
          zIndex: 3,
          background: rowBg,
          boxShadow: "1px 0 0 #eee",
        }
      : null;

  const isEditingThis = (col) => editingCell?.id === item.id && editingCell?.col === col;

  // ✅ Action button styles removed - now using right-click context menu

  // ✅ LOCK CHECK HELPER for inline edits
  const checkLockBeforeEdit = (e, callback) => {
    if (locked) {
      stop(e);
      showAlert("This content has been built by the operator and is locked. Contact your Lumen8 content manager to unlock it.", "warning");
      return;
    }
    callback(e);
  };

  // ============================================================================
  // INLINE EDITORS (with lock checks added)
  // ============================================================================

  function InlineText({ col, value, field, textarea = false }) {
    const ltrStyle = {
      direction: "ltr",
      unicodeBidi: "isolate",
      writingMode: "horizontal-tb",
      textAlign: "left",
    };

    // Special handling for Club Instructions textarea - use modal instead of inline editing
    if (textarea && field === "clubInstructions") {
      const openModal = () => {
        if (isBusy || locked) return;
        
        // Create a simple modal
        const currentValue = value || "";
        const modal = document.createElement("div");
        modal.style.cssText = `
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(0, 0, 0, 0.5);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 10000;
          padding: 20px;
        `;
        
        const modalContent = document.createElement("div");
        modalContent.style.cssText = `
          background: white;
          border-radius: 18px;
          padding: 24px;
          max-width: 600px;
          width: 100%;
          box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
        `;
        
        const title = document.createElement("h3");
        title.textContent = "Edit Club Instructions";
        title.style.cssText = `
          margin: 0 0 16px 0;
          font-size: 18px;
          font-weight: 1000;
          color: ${THEME.text};
        `;
        
        const textareaEl = document.createElement("textarea");
        textareaEl.value = currentValue.trim();
        textareaEl.style.cssText = `
          width: 100%;
          min-height: 120px;
          border: 1px solid ${THEME.line};
          border-radius: 12px;
          padding: 12px;
          font-size: 14px;
          font-family: inherit;
          box-sizing: border-box;
          resize: vertical;
          direction: ltr;
          unicode-bidi: isolate;
          margin-bottom: 16px;
        `;
        textareaEl.dir = "ltr";
        textareaEl.lang = "en";
        textareaEl.spellcheck = true;
        
        const buttonContainer = document.createElement("div");
        buttonContainer.style.cssText = `
          display: flex;
          gap: 12px;
          justify-content: flex-end;
        `;
        
        const cancelBtn = document.createElement("button");
        cancelBtn.textContent = "Cancel";
        cancelBtn.style.cssText = `
          border: 1px solid ${THEME.line};
          background: white;
          color: ${THEME.text};
          border-radius: 12px;
          padding: 10px 16px;
          cursor: pointer;
          font-weight: 900;
          font-size: 13px;
        `;
        cancelBtn.onclick = () => document.body.removeChild(modal);
        
        const saveBtn = document.createElement("button");
        saveBtn.textContent = "Save";
        saveBtn.style.cssText = `
          border: 1px solid ${THEME.primary};
          background: ${THEME.primary};
          color: ${THEME.dark};
          border-radius: 12px;
          padding: 10px 16px;
          cursor: pointer;
          font-weight: 900;
          font-size: 13px;
        `;
        saveBtn.onclick = async () => {
          const newValue = textareaEl.value;
          document.body.removeChild(modal);
          if (newValue !== currentValue) {
            await onInlineSave(item.id, { [field]: newValue });
          }
        };
        
        buttonContainer.appendChild(cancelBtn);
        buttonContainer.appendChild(saveBtn);
        
        modalContent.appendChild(title);
        modalContent.appendChild(textareaEl);
        modalContent.appendChild(buttonContainer);
        
        modal.appendChild(modalContent);
        modal.onclick = (e) => {
          if (e.target === modal) document.body.removeChild(modal);
        };
        
        document.body.appendChild(modal);
        textareaEl.focus();
        textareaEl.setSelectionRange(0, 0);
      };

      return (
        <div
          role="button"
          tabIndex={0}
          onClick={(e) => checkLockBeforeEdit(e, () => { stop(e); openModal(); })}
          style={{
            width: "100%",
            whiteSpace: "pre-wrap",
            overflow: "hidden",
            cursor: locked ? "not-allowed" : "pointer",
            minHeight: 40,
            opacity: locked ? 0.6 : 1,
            ...ltrStyle,
          }}
          dir="ltr"
          title={locked ? "🔒 Built by operator — contact Lumen8 to unlock" : (value ?? "Click to edit...")}
        >
          {value ? highlightText(value, searchTerm) : <span style={{ opacity: 0.5 }}>Click to edit…</span>}
        </div>
      );
    }

    // Regular inline editing for non-textarea fields
    if (!isEditingThis(col)) {
      const readonlyStyle = textarea
        ? {
            width: "100%",
            whiteSpace: "pre-wrap",
            overflow: "hidden",
            cursor: locked ? "not-allowed" : "text",
            minHeight: 40,
            opacity: locked ? 0.6 : 1,
            ...ltrStyle,
          }
        : {
            width: "100%",
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
            cursor: locked ? "not-allowed" : "text",
            opacity: locked ? 0.6 : 1,
            ...ltrStyle,
          };

      return (
        <div
          role="button"
          tabIndex={0}
          onClick={(e) => checkLockBeforeEdit(e, () => {
            stop(e);
            if (isBusy) return;
            setEditingCell({ id: item.id, col, draft: value ?? "" });
          })}
          style={readonlyStyle}
          dir="ltr"
          title={locked ? "🔒 Built by operator — contact Lumen8 to unlock" : (value ?? "")}
        >
          {value ? highlightText(value, searchTerm) : <span style={{ opacity: 0.5, cursor: "text" }}>Click to edit…</span>}
        </div>
      );
    }

    const save = async () => {
      const draft = String(editingCell?.draft ?? "");
      setEditingCell(null);
      if (String(value ?? "") === draft) return;
      await onInlineSave(item.id, { [field]: draft });
    };

    const cancel = () => {
      setEditingCell(null);
    };

    const textareaStyle = {
      width: "100%",
      border: `1px solid ${THEME.line}`,
      borderRadius: 16,
      padding: "10px 12px",
      boxSizing: "border-box",
      direction: "ltr",
      unicodeBidi: "isolate",
      writingMode: "horizontal-tb",
      textAlign: "left",
      resize: "vertical",
      fontSize: 13,
    };

    const inputStyle = {
      width: "100%",
      border: `1px solid ${THEME.line}`,
      borderRadius: 12,
      padding: "8px 10px",
      fontSize: 13,
      color: THEME.text,
      boxSizing: "border-box",
      direction: "ltr",
      unicodeBidi: "isolate",
      writingMode: "horizontal-tb",
      textAlign: "left",
    };

    if (textarea) {
      return (
        <div style={{ width: "100%", display: "flex", flexDirection: "column", gap: 8 }}>
          <textarea
            value={editingCell?.draft ?? ""}
            onChange={(e) => setEditingCell((p) => ({ ...p, draft: e.target.value }))}
            onKeyDown={async (e) => {
              if (e.key === "Escape") {
                stop(e);
                cancel();
              }
            }}
            rows={2}
            style={textareaStyle}
            dir="ltr"
            lang="en"
            spellCheck
            autoFocus
            placeholder="Click to edit…"
          />
          <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
            <button
              type="button"
              onClick={cancel}
              style={{
                border: `1px solid ${THEME.line}`,
                background: "white",
                color: THEME.text,
                borderRadius: 12,
                padding: "6px 12px",
                cursor: "pointer",
                fontWeight: 900,
                fontSize: 12,
              }}
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={save}
              style={{
                border: `1px solid ${THEME.primary}`,
                background: THEME.primary,
                color: "white",
                borderRadius: 12,
                padding: "6px 12px",
                cursor: "pointer",
                fontWeight: 900,
                fontSize: 12,
              }}
            >
              Save
            </button>
          </div>
        </div>
      );
    }

    return (
      <input
        value={editingCell?.draft ?? ""}
        onChange={(e) => setEditingCell((p) => ({ ...p, draft: e.target.value }))}
        onBlur={save}
        onKeyDown={async (e) => {
          if (e.key === "Escape") {
            stop(e);
            cancel();
          }
          if (e.key === "Enter") {
            stop(e);
            await save();
          }
        }}
        style={inputStyle}
        dir="ltr"
        lang="en"
        spellCheck
        autoFocus
      />
    );
  }

  // InlineNumber was hoisted to module scope as RowNumberCell / InlineNumberEditor
  // (stable identity + local draft state) to stop per-keystroke remounts.


  // ============================================================================
  // InlineAsset - uses standalone component to avoid re-render remounting
  // ============================================================================
  const renderInlineAsset = (col, currentId, patchKey) => (
    <InlineAssetStable
      col={col}
      currentId={currentId}
      patchKey={patchKey}
      assetCache={assetCache}
      editingCell={editingCell}
      itemId={item.id}
      item={item}
      isBusy={isBusy}
      locked={locked}
      setEditingCell={setEditingCell}
      onInlineSave={onInlineSave}
      onCopy={onCopy}
      onMove={onMove}
      onDelete={onDelete}
      categories={categories}
      searchTerm={searchTerm}
    />
  );

  function InlineMulti({ col, value, patchKey, options }) {
    const isIptvDeferred = isIptvMode && (col === "selectTiming" || col === "selectLocation");

    if (!isEditingThis(col)) {
      return (
        <div
          role="button"
          tabIndex={0}
          onClick={(e) => checkLockBeforeEdit(e, () => {
            stop(e);
            if (isBusy) return;

            if (isIptvDeferred) {
              setEditingCell({ id: item.id, col, draft: uniqArr(value) });
            } else {
              setEditingCell({ id: item.id, col });
            }
          })}
          style={{
            width: "100%",
            direction: "ltr",
            unicodeBidi: "isolate",
            writingMode: "horizontal-tb",
            textAlign: "left",
            minWidth: 0,
            cursor: locked ? "not-allowed" : "pointer",
            opacity: locked ? 0.6 : 1,
          }}
          title={locked ? "🔒 Built by operator — contact Lumen8 to unlock" : uniqArr(value).join(", ")}
        >
          {uniqArr(value).length ? (
            <div style={{ display: "flex", flexWrap: "wrap", gap: 6, minWidth: 0 }}>
              {uniqArr(value)
                .slice(0, 3)
                .map((s) => (
                  <span
                    key={s}
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      padding: "5px 10px",
                      borderRadius: 999,
                      border: `1px solid ${THEME.line}`,
                      background: THEME.surface2,
                      fontWeight: 900,
                      fontSize: 12,
                      whiteSpace: "nowrap",
                    }}
                  >
                    {s}
                  </span>
                ))}
              {uniqArr(value).length > 3 ? (
                <span
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    padding: "5px 10px",
                    borderRadius: 999,
                    border: `1px solid ${THEME.line}`,
                    background: THEME.surface2,
                    fontWeight: 900,
                    fontSize: 12,
                    opacity: 0.85,
                    whiteSpace: "nowrap",
                  }}
                >
                  +{uniqArr(value).length - 3}
                </span>
              ) : null}
            </div>
          ) : (
            <div style={{ opacity: 0.55 }}>Click to select…</div>
          )}
        </div>
      );
    }

    if (isIptvDeferred) {
      const selectedDraft = uniqArr(editingCell?.draft);

      const toggle = (opt) => {
        const next = new Set(selectedDraft);
        if (next.has(opt)) next.delete(opt);
        else next.add(opt);
        setEditingCell((p) => ({ ...p, draft: Array.from(next) }));
      };

      const pill = (txt) => ({
        display: "inline-flex",
        alignItems: "center",
        padding: "5px 10px",
        borderRadius: 999,
        border: `1px solid ${THEME.line}`,
        background: THEME.surface2,
        fontWeight: 900,
        fontSize: 12,
        whiteSpace: "nowrap",
      });

      return (
        <div
          onMouseDown={(e) => e.stopPropagation()}
          onClick={(e) => e.stopPropagation()}
          style={{ width: "100%", minWidth: 0 }}
        >
          <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
            <button
              type="button"
              onClick={async () => {
                const nextArr = [];
                setEditingCell(null);
                await onInlineSave(item.id, { [patchKey]: cleanMulti(nextArr) });
              }}
              style={{
                border: `1px solid ${THEME.line}`,
                background: "white",
                borderRadius: 12,
                padding: "7px 10px",
                cursor: "pointer",
                fontWeight: 900,
                fontSize: 12,
                boxSizing: "border-box",
              }}
            >
              Clear
            </button>
            <button
              type="button"
              onClick={async () => {
                const nextArr = cleanMulti(selectedDraft);
                setEditingCell(null);
                await onInlineSave(item.id, { [patchKey]: nextArr });
              }}
              style={{
                border: `1px solid ${THEME.line}`,
                background: "white",
                borderRadius: 12,
                padding: "7px 10px",
                cursor: "pointer",
                fontWeight: 900,
                fontSize: 12,
                boxSizing: "border-box",
              }}
            >
              Done
            </button>
          </div>

          <div style={{ marginTop: 8, minWidth: 0 }}>
            {selectedDraft.length ? (
              <div style={{ display: "flex", flexWrap: "wrap", gap: 6, minWidth: 0 }}>
                {selectedDraft.slice(0, 3).map((s) => (
                  <span key={s} style={pill(s)}>
                    {s}
                  </span>
                ))}
                {selectedDraft.length > 3 ? (
                  <span style={{ ...pill(`+${selectedDraft.length - 3}`), opacity: 0.85 }}>
                    +{selectedDraft.length - 3}
                  </span>
                ) : null}
              </div>
            ) : (
              <div style={{ opacity: 0.55 }}>Tick multiple then Done…</div>
            )}
          </div>

          <div
            style={{
              marginTop: 8,
              maxHeight: 230,
              overflowY: "auto",
              border: `1px solid ${THEME.line}`,
              borderRadius: 12,
              padding: 10,
              background: "white",
              boxSizing: "border-box",
            }}
          >
            {options.map((opt) => {
              const checked = selectedDraft.includes(opt);
              return (
                <label
                  key={opt}
                  style={{
                    display: "flex",
                    gap: 10,
                    alignItems: "center",
                    padding: "7px 6px",
                    borderRadius: 10,
                    cursor: "pointer",
                    userSelect: "none",
                    background: checked ? THEME.chip : "transparent",
                  }}
                >
                  <input
                    type="checkbox"
                    checked={checked}
                    onChange={() => toggle(opt)}
                    style={{ transform: "scale(1.05)" }}
                  />
                  <span style={{ fontSize: 13, fontWeight: 900 }}>{opt}</span>
                </label>
              );
            })}
          </div>
        </div>
      );
    }

    return (
      <MultiSelectInline
        options={options}
        value={value}
        disabled={isBusy}
        onChange={async (arr) => {
          await onInlineSave(item.id, { [patchKey]: cleanMulti(arr) });
        }}
      />
    );
  }

  function StatusPill({ value }) {
    const v = String(value || "").trim() || "—";
    const bg = statusRowColor(value);
    const br = statusBorderColor(value);
    return (
      <div style={{ width: "100%", minWidth: 0 }} title={v}>
        <span
          style={{
            display: "inline-flex",
            alignItems: "center",
            padding: "6px 10px",
            borderRadius: 999,
            border: `1px solid ${br}`,
            background: bg,
            fontWeight: 900,
            fontSize: 12,
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
            maxWidth: "100%",
            boxSizing: "border-box",
          }}
        >
          {v}
        </span>
      </div>
    );
  }

  // ✅ Comment Popup Modal for viewing full Lumen8 Comments
  function CommentPopup({ value, onClose }) {
    if (!value) return null;
    
    return (
      <>
        {/* Backdrop */}
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.6)',
            zIndex: 9999,
          }}
          onClick={onClose}
        />
        {/* Modal */}
        <div
          style={{
            position: 'fixed',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            background: '#1a1a2e',
            borderRadius: '12px',
            padding: '24px',
            minWidth: '400px',
            maxWidth: '600px',
            maxHeight: '80vh',
            overflow: 'auto',
            zIndex: 10000,
            boxShadow: '0 20px 60px rgba(0, 0, 0, 0.5)',
            border: '1px solid rgba(45, 212, 191, 0.3)',
          }}
        >
          {/* Header */}
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '16px',
            paddingBottom: '12px',
            borderBottom: '1px solid rgba(255,255,255,0.1)',
          }}>
            <h3 style={{
              margin: 0,
              fontSize: '16px',
              fontWeight: 600,
              color: '#fff',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
            }}>
              <span style={{ color: '#2dd4bf' }}>💬</span>
              Lumen8 Comments
            </h3>
            <button
              onClick={onClose}
              style={{
                background: 'none',
                border: 'none',
                color: '#a1a1aa',
                fontSize: '20px',
                cursor: 'pointer',
                padding: '4px 8px',
                borderRadius: '4px',
              }}
              onMouseOver={(e) => e.target.style.background = 'rgba(255,255,255,0.1)'}
              onMouseOut={(e) => e.target.style.background = 'none'}
            >
              ✕
            </button>
          </div>
          {/* Content */}
          <div style={{
            fontSize: '14px',
            lineHeight: '1.6',
            color: '#e5e5e5',
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-word',
          }}>
            {value}
          </div>
        </div>
      </>
    );
  }

  function ReadonlyComments({ value, onClick }) {
    const ltrStyle = {
      direction: "ltr",
      unicodeBidi: "isolate",
      writingMode: "horizontal-tb",
      textAlign: "left",
    };

    const hasValue = Boolean(value);

    return (
      <div
        style={{
          width: "100%",
          minWidth: 0,
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis",
          opacity: hasValue ? 1 : 0.55,
          cursor: hasValue ? "pointer" : "default",
          ...ltrStyle,
        }}
        dir="ltr"
        title={hasValue ? "Click to view full comment" : ""}
        onClick={hasValue ? onClick : undefined}
      >
        {hasValue ? (
          <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
            <span style={{ 
              color: '#2dd4bf', 
              fontSize: '12px',
              flexShrink: 0,
            }}>💬</span>
            {highlightText(value, searchTerm)}
          </span>
        ) : "—"}
      </div>
    );
  }

  const show = (key) => visibleKeys.has(key);

  // ============================================================================
  // RENDER CELLS
  // ============================================================================

  return (
    <>
      {/* ✅ Context Menu */}
      {contextMenu && (
        <ContextMenu
          x={contextMenu.x}
          y={contextMenu.y}
          onClose={closeContextMenu}
          onCopy={() => onCopy(item)}
          onMove={onMove}
          onDelete={() => onDelete(item)}
          locked={locked}
          item={item}
          categories={categories}
        />
      )}

      <div 
        ref={setNodeRef} 
        style={{ 
          ...rowStyle, 
          marginTop: 10,
          // Add glow when someone else is editing
          ...(editingUser ? {
            boxShadow: `0 0 0 2px ${editingUser.color}50, 0 0 12px ${editingUser.color}30`,
          } : {}),
        }}
        onContextMenu={handleContextMenu}
        onMouseEnter={() => setRowHovered(true)}
        onMouseLeave={() => setRowHovered(false)}
      >
        {/* Presence indicator - shows who's editing this row */}
        {editingUser && (
          <div style={{
            position: 'absolute',
            top: -10,
            right: 12,
            background: editingUser.color,
            color: '#fff',
            fontSize: 10,
            fontWeight: 600,
            padding: '3px 10px',
            borderRadius: 12,
            display: 'flex',
            alignItems: 'center',
            gap: 6,
            boxShadow: '0 2px 8px rgba(0,0,0,0.25)',
            zIndex: 20,
            animation: 'presenceFadeIn 0.2s ease',
          }}>
            {editingUser.avatar ? (
              <img 
                src={editingUser.avatar} 
                alt={editingUser.name}
                style={{ width: 16, height: 16, borderRadius: '50%', objectFit: 'cover' }}
              />
            ) : (
              <span style={{ 
                width: 16, 
                height: 16, 
                borderRadius: '50%', 
                background: 'rgba(255,255,255,0.3)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: 8,
              }}>
                {getInitials(editingUser.name)}
              </span>
            )}
            <span>{editingUser.name.split(' ')[0]} editing</span>
          </div>
        )}
        
        {show("select") ? (
          <div style={{ ...cellBase, ...(stickyStyle("select") || {}), justifyContent: "center" }}>
            <input
              type="checkbox"
              checked={isSelected || false}
              onChange={(e) => {
                e.stopPropagation();
                if (onToggleSelect) {
                  onToggleSelect(item.id);
                }
              }}
              disabled={isBusy}
              style={{
                width: 18,
                height: 18,
                cursor: isBusy ? "not-allowed" : "pointer",
                accentColor: THEME.primary,
              }}
              title={isSelected ? "Deselect row" : "Select row for bulk operations"}
            />
          </div>
        ) : null}

      {show("drag") ? (
        <div style={{ ...cellBase, ...(stickyStyle("drag") || {}), justifyContent: "center" }}>
          <div
            ref={setActivatorNodeRef}
            {...attributes}
            {...listeners}
            onMouseDown={(e) => e.stopPropagation()}
            style={{
              cursor: "grab",
              border: `1px solid ${THEME.line}`,
              borderRadius: 12,
              padding: "6px 10px",
              width: 48,
              textAlign: "center",
              userSelect: "none",
              background: "white",
              color: THEME.primary,
              fontWeight: 900,
              boxSizing: "border-box",
            }}
            title="Drag to reorder"
          >
            ⇅
          </div>
        </div>
      ) : null}

      {show("cue") ? (
        <div
          style={{
            ...cellBase,
            ...(stickyStyle("cue") || {}),
            justifyContent: "center",
            fontWeight: 900,
          }}
        >
          {item.cue ?? ""}
        </div>
      ) : null}

      {show("name") ? (
        <div style={cellBase}>
          <RowTextCell col="name" field="name" value={item.name} item={item} locked={locked} isBusy={isBusy} editingCell={editingCell} setEditingCell={setEditingCell} onInlineSave={onInlineSave} searchTerm={searchTerm} />
        </div>
      ) : null}

      {show("playerNumber") ? (
        <div style={cellBase}>
          <RowTextCell col="playerNumber" field="playerNumber" value={item.playerNumber} item={item} locked={locked} isBusy={isBusy} editingCell={editingCell} setEditingCell={setEditingCell} onInlineSave={onInlineSave} searchTerm={searchTerm} />
        </div>
      ) : null}

      {show("clientSponsor") ? (
        <div style={cellBase}>
          <RowTextCell col="clientSponsor" field="clientSponsorName" value={item.clientSponsorName} item={item} locked={locked} isBusy={isBusy} editingCell={editingCell} setEditingCell={setEditingCell} onInlineSave={onInlineSave} searchTerm={searchTerm} />
        </div>
      ) : null}

      {/* ✅ COLS ORDER: contentStatus, notes come right after clientSponsor */}
      {show("contentStatus") ? (
        <div style={cellBase}>
          <StatusPill value={item.contentStatus} />
        </div>
      ) : null}

      {show("notes") ? (
        <div style={cellBase}>
          <ReadonlyComments value={item.notes} onClick={() => setShowCommentPopup(true)} />
        </div>
      ) : null}

      {/* ✅ COLS ORDER: Content columns - bigGfxVideos, cornerGfxVideos, ledAllTypes */}
      {show("bigGfxVideos") ? (
        <div style={cellBase}>
          {renderInlineAsset("bigGfxVideos", item.bigGfxVideosId, "bigGfxVideosId")}
        </div>
      ) : null}

      {show("cornerGfxVideos") ? (
        <div style={cellBase}>
          {renderInlineAsset("cornerGfxVideos", item.cornerGfxVideosId, "cornerGfxVideosId")}
        </div>
      ) : null}

      {show("ledAllTypes") ? (
        <div style={cellBase}>
          {renderInlineAsset("ledAllTypes", item.ledAllTypesId, "ledAllTypesId")}
        </div>
      ) : null}

      {/* ✅ COLS ORDER: TVC columns */}
      {show("bigTvc") ? (
        <div style={cellBase}>
          {renderInlineAsset("bigTvc", item.bigTvcId, "bigTvcId")}
        </div>
      ) : null}

      {show("cornerTvc") ? (
        <div style={cellBase}>
          {renderInlineAsset("cornerTvc", item.cornerTvcId, "cornerTvcId")}
        </div>
      ) : null}

      {/* ✅ COLS ORDER: SuperFull comes before IPTV columns */}
      {show("superFull") ? (
        <div style={cellBase}>
          {renderInlineAsset("superFull", item.superFullId, "superFullId")}
        </div>
      ) : null}

      {/* ✅ COLS ORDER: IPTV columns */}
      {show("iptvGfx") ? (
        <div style={cellBase}>
          {renderInlineAsset("iptvGfx", item.iptvGfxId, "iptvGfxId")}
        </div>
      ) : null}

      {show("rotations") ? (
        <div style={cellBase}>
          <RowNumberCell col="rotations" field="rotations" value={item.rotations} min={0} step={1} item={item} locked={locked} isBusy={isBusy} editingCell={editingCell} setEditingCell={setEditingCell} onInlineSave={onInlineSave} />
        </div>
      ) : null}

      {show("bonusPriority") ? (
        <div style={cellBase}>
          <RowNumberCell col="bonusPriority" field="bonusPriority" value={item.bonusPriority} min={1} step={1} item={item} locked={locked} isBusy={isBusy} editingCell={editingCell} setEditingCell={setEditingCell} onInlineSave={onInlineSave} />
        </div>
      ) : null}

      {show("selectTiming") ? (
        <div style={cellBase}>
          <InlineMulti
            col="selectTiming"
            value={item.selectTiming}
            patchKey="selectTiming"
            options={iptvOptions?.timing || []}
          />
        </div>
      ) : null}

      {show("selectLocation") ? (
        <div style={cellBase}>
          <InlineMulti
            col="selectLocation"
            value={item.selectLocation}
            patchKey="selectLocation"
            options={iptvOptions?.location || []}
          />
        </div>
      ) : null}

      {show("allocationId") ? (
        <div style={cellBase}>
          <RowTextCell col="allocationId" field="allocationId" value={item.allocationId} item={item} locked={locked} isBusy={isBusy} editingCell={editingCell} setEditingCell={setEditingCell} onInlineSave={onInlineSave} searchTerm={searchTerm} />
        </div>
      ) : null}

      {/* ✅ COLS ORDER: Remaining LED columns */}
      {show("mcgTiledLed") ? (
        <div style={cellBase}>
          {renderInlineAsset("mcgTiledLed", item.mcgTiledLedId, "mcgTiledLedId")}
        </div>
      ) : null}

      {show("marvelTiledLed") ? (
        <div style={cellBase}>
          {renderInlineAsset("marvelTiledLed", item.marvelTiledLedId, "marvelTiledLedId")}
        </div>
      ) : null}

      {show("super169") ? (
        <div style={cellBase}>
          {renderInlineAsset("super169", item.super169Id, "super169Id")}
        </div>
      ) : null}

      {show("superSide") ? (
        <div style={cellBase}>
          {renderInlineAsset("superSide", item.superSideId, "superSideId")}
        </div>
      ) : null}

      {show("mcgInterchange") ? (
        <div style={cellBase}>
          {renderInlineAsset("mcgInterchange", item.mcgInterchangeId, "mcgInterchangeId")}
        </div>
      ) : null}

      {show("marvelInterchange") ? (
        <div style={cellBase}>
          {renderInlineAsset("marvelInterchange", item.marvelInterchangeId, "marvelInterchangeId")}
        </div>
      ) : null}

      {show("marvel360") ? (
        <div style={cellBase}>
          {renderInlineAsset("marvel360", item.marvel360Id, "marvel360Id")}
        </div>
      ) : null}

      {show("mcg360") ? (
        <div style={cellBase}>
          {renderInlineAsset("mcg360", item.mcg360Id, "mcg360Id")}
        </div>
      ) : null}

      {show("clubInstructions") ? (
        <div style={cellBase}>
          <InlineText col="clubInstructions" value={item.clubInstructions} field="clubInstructions" textarea />
        </div>
      ) : null}

      {/* ⚠️ No content warning — row has instructions but no CG or LED asset linked */}
      {item.clubInstructions && item.clubInstructions.trim() && !item.bigGfxVideosId && !item.ledAllTypesId && !item.iptvGfxId && !isExampleContent && (
        <div style={{
          position: 'absolute',
          right: 8,
          bottom: 4,
          display: 'flex',
          alignItems: 'center',
          gap: 4,
          padding: '3px 8px',
          borderRadius: 5,
          background: '#f97316',
          color: '#fff',
          fontSize: 9,
          fontWeight: 700,
          zIndex: 5,
          boxShadow: '0 2px 6px rgba(249,115,22,0.3)',
          letterSpacing: '0.2px',
        }}
          title="This row has instructions but no CG or LED asset linked. It will NOT appear in the operator playout view. Please allocate content from your library."
        >
          ⚠ NO CONTENT — WON'T REACH OPERATOR
        </div>
      )}

      {/* 📦 Archived content warning — allocated asset has been archived from library */}
      {!isExampleContent && (() => {
        const assetIds = [
          item.bigGfxVideosId, item.ledAllTypesId, item.cornerGfxVideosId,
          item.bigTvcId, item.cornerTvcId, item.iptvGfxId,
          item.mcgTiledLedId, item.marvelTiledLedId, item.superFullId,
          item.super169Id, item.superSideId, item.mcgInterchangeId,
          item.marvelInterchangeId, item.marvel360Id, item.mcg360Id,
        ].filter(Boolean);
        const archivedAssets = assetIds
          .map(id => assetCache.get(id))
          .filter(a => a && a.archived);
        if (archivedAssets.length === 0) return null;
        const lumenIds = archivedAssets.map(a => a.lumenId || a.title || a.id).filter(Boolean);
        const names = archivedAssets.map(a => a.title || a.id).join(', ');
        return (
          <div style={{
            position: 'absolute',
            right: 8,
            bottom: item.clubInstructions && item.clubInstructions.trim() && !item.bigGfxVideosId && !item.ledAllTypesId ? 26 : 4,
            display: 'flex',
            alignItems: 'center',
            gap: 4,
            padding: '3px 8px',
            borderRadius: 5,
            background: '#7f1d1d',
            border: '1px solid #ef4444',
            color: '#fecaca',
            fontSize: 9,
            fontWeight: 700,
            zIndex: 5,
            boxShadow: '0 2px 6px rgba(239,68,68,0.2)',
            letterSpacing: '0.2px',
            maxWidth: 400,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
            title={`Archived content: ${names}. This asset has been archived from the content library and will not go out for playout. Please replace with active content.`}
          >
            📦 ARCHIVED: {lumenIds.join(', ')}
          </div>
        );
      })()}

      {/* ❗ Rejected content badge — shows which Lumen8 IDs are on the rejected row */}
      {!isExampleContent && item.contentStatus === 'Rejected' && (() => {
        const assetIds = [
          item.bigGfxVideosId, item.ledAllTypesId, item.cornerGfxVideosId,
          item.bigTvcId, item.cornerTvcId, item.iptvGfxId,
          item.mcgTiledLedId, item.marvelTiledLedId, item.superFullId,
          item.super169Id, item.superSideId, item.mcgInterchangeId,
          item.marvelInterchangeId, item.marvel360Id, item.mcg360Id,
        ].filter(Boolean);
        const allocatedAssets = assetIds
          .map(id => assetCache.get(id))
          .filter(Boolean);
        if (allocatedAssets.length === 0) return null;
        const lumenIds = allocatedAssets
          .map(a => a.lumenId || a.title || a.id)
          .filter(Boolean);
        return (
          <div style={{
            position: 'absolute',
            right: 8,
            bottom: 4,
            display: 'flex',
            alignItems: 'center',
            gap: 4,
            padding: '3px 8px',
            borderRadius: 5,
            background: '#7f1d1d',
            border: '1px solid #ef4444',
            color: '#fecaca',
            fontSize: 9,
            fontWeight: 700,
            zIndex: 5,
            boxShadow: '0 2px 6px rgba(239,68,68,0.2)',
            letterSpacing: '0.2px',
            maxWidth: 400,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
            title={`Rejected content: ${lumenIds.join(', ')}. Replace or update the flagged asset and resubmit.`}
          >
            ❗ REJECTED: {lumenIds.join(', ')}
          </div>
        );
      })()}

      {/* ✅ History Icon - shows last modified info on hover */}
      {rowHovered && (item.lastModifiedTime || item.venueFlowEditor) && (
        <div
          style={{
            position: 'absolute',
            right: 8,
            top: '50%',
            transform: 'translateY(-50%)',
            background: THEME.surface,
            border: `1px solid ${THEME.line}`,
            borderRadius: 6,
            padding: '4px 8px',
            display: 'flex',
            alignItems: 'center',
            gap: 4,
            fontSize: 10,
            color: THEME.textSecondary,
            boxShadow: THEME.shadowSm,
            zIndex: 10,
            cursor: 'default',
            whiteSpace: 'nowrap',
          }}
          title={(() => {
            const lines = ['📋 Row History'];
            if (item.venueFlowEditor) {
              lines.push(`Last edited by: ${item.venueFlowEditor}`);
            }
            if (item.lastModifiedTime) {
              const date = new Date(item.lastModifiedTime);
              lines.push(`Date: ${date.toLocaleDateString()}`);
              lines.push(`Time: ${date.toLocaleTimeString()}`);
            }
            return lines.join('\n');
          })()}
        >
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="12" cy="12" r="10" />
            <polyline points="12 6 12 12 16 14" />
          </svg>
          <span>
            {item.venueFlowEditor && (
              item.venueFlowEditor.split(' ')[0]
            )}
            {item.lastModifiedTime && (
              <> · {new Date(item.lastModifiedTime).toLocaleDateString()}</>
            )}
          </span>
        </div>
      )}

      {/* ✅ Actions column removed - now using right-click context menu */}
      </div>
      
      {/* ✅ Comment Popup for viewing full Lumen8 Comments */}
      {showCommentPopup && item.notes && (
        <CommentPopup value={item.notes} onClose={() => setShowCommentPopup(false)} />
      )}
    </>
  );
}
